import React, { useState, useEffect, useCallback } from 'react';
import { getRelatorioPeriodo, getPedido } from '../api';

const PAGE_SIZE = 20;

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function fmt(v) {
  const n = Number(v);
  if (isNaN(n)) return 'R$ 0,00';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function fmtData(d) {
  const p = String(d).slice(0,10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : '';
}
function hoje() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

function agruparPorProduto(movs) {
  const m = {};
  for (const x of movs) {
    const k = x.produto_nome;
    if (!m[k]) m[k] = { nome: k, vendas: 0, receita: 0, custo: 0 };
    if (x.tipo === 'entrada') m[k].custo += +x.valor_total;
    else { m[k].vendas += +x.quantidade; m[k].receita += +x.valor_total; }
  }
  return Object.values(m).filter(p => p.vendas > 0).sort((a,b) => b.receita - a.receita);
}

function Paginacao({ total, page, onPage }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const items = [];
  for (let i = Math.max(1, page-2); i <= Math.min(pages, page+2); i++) items.push(i);
  if (items[0] > 1) { items.unshift(1); if (items[1] > 2) items.splice(1,0,'...'); }
  if (items[items.length-1] < pages) { if (items[items.length-1] < pages-1) items.push('...'); items.push(pages); }
  return (
    <div className="btn-g" style={{ justifyContent: 'center', marginTop: 12 }}>
      <button className="btn btn3 btn-sm" disabled={page<=1} onClick={()=>onPage(1)}>«</button>
      <button className="btn btn3 btn-sm" disabled={page<=1} onClick={()=>onPage(page-1)}>‹</button>
      {items.map((p,i) => typeof p === 'number'
        ? <button key={i} className={`btn btn-sm ${page===p?'btn1':'btn3'}`} onClick={()=>onPage(p)}>{p}</button>
        : <span key={i} style={{ padding:'4px 2px', color:'var(--c3)' }}>…</span>
      )}
      <button className="btn btn3 btn-sm" disabled={page>=pages} onClick={()=>onPage(page+1)}>›</button>
      <button className="btn btn3 btn-sm" disabled={page>=pages} onClick={()=>onPage(pages)}>»</button>
    </div>
  );
}

const PRESETS = [
  { label: 'Este mês',    fn: () => { const h=hoje(); return [new Date(h.getFullYear(),h.getMonth(),1), h]; } },
  { label: 'Mês passado', fn: () => { const h=hoje(); return [new Date(h.getFullYear(),h.getMonth()-1,1), new Date(h.getFullYear(),h.getMonth(),0)]; } },
  { label: '3 meses',     fn: () => { const h=hoje(); return [new Date(h.getFullYear(),h.getMonth()-2,1), h]; } },
  { label: '6 meses',     fn: () => { const h=hoje(); return [new Date(h.getFullYear(),h.getMonth()-5,1), h]; } },
  { label: 'Este ano',    fn: () => { const h=hoje(); return [new Date(h.getFullYear(),0,1), h]; } },
];

const SUB = [
  { key: 'pedidos', label: '🛒 Pedidos' },
  { key: 'margem',  label: '📊 Produtos' },
  { key: 'estoque', label: '📦 Entradas' },
  { key: 'custos',  label: '🧾 Despesas' },
];

export default function RelatorioFinanceiro() {
  const h = hoje();
  const [inicio, setInicio] = useState(toDateStr(new Date(h.getFullYear(), h.getMonth(), 1)));
  const [fim, setFim] = useState(toDateStr(h));
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [subTab, setSubTab] = useState('pedidos');
  const [page, setPage] = useState({ pedidos:1, estoque:1, custos:1 });
  const [pedidoModal, setPedidoModal] = useState(null);
  const [presetAtivo, setPresetAtivo] = useState(0);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(''); setPage({ pedidos:1, estoque:1, custos:1 });
    try { setDados(await getRelatorioPeriodo(inicio||undefined, fim||undefined)); }
    catch (err) { setErro(err.message); }
    setLoading(false);
  }, [inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const aplicarPreset = (idx) => {
    setPresetAtivo(idx);
    const [s, e] = PRESETS[idx].fn();
    setInicio(toDateStr(s));
    setFim(toDateStr(e));
  };

  const movs     = dados?.movimentacoes || [];
  const custos   = dados?.custos || [];
  const pedidos  = dados?.pedidos || [];
  const vendas   = movs.filter(m => m.tipo === 'saida');
  const estoques = movs.filter(m => m.tipo === 'entrada');
  const margem   = dados ? agruparPorProduto(movs) : [];
  const getPg    = (k) => page[k] || 1;
  const setPg    = (k,v) => setPage(p => ({ ...p, [k]: v }));

  const totalReceita  = dados?.total_saidas || 0;
  const totalInsumos  = dados?.total_entradas || 0;
  const totalCustos   = dados?.total_custos || 0;
  const lucro         = dados?.lucro || 0;
  const totalGastos   = totalInsumos + totalCustos;
  const barTotal      = totalReceita + totalGastos;
  const pctReceita    = barTotal > 0 ? (totalReceita / barTotal) * 100 : 0;
  const pctInsumos    = barTotal > 0 ? (totalInsumos  / barTotal) * 100 : 0;
  const pctCustos     = barTotal > 0 ? (totalCustos   / barTotal) * 100 : 0;
  const margemPct     = totalReceita > 0 ? (lucro / totalReceita) * 100 : 0;
  const ticketMedio   = pedidos.length > 0 ? totalReceita / pedidos.length : 0;
  const totalUnids    = vendas.reduce((s,v) => s + +v.quantidade, 0);
  const topProduto    = margem[0];
  const maxReceita    = margem.length > 0 ? margem[0].receita : 1;

  /* agrupamento de categorias de custos */
  const catCustos = custos.reduce((acc, c) => {
    acc[c.categoria] = (acc[c.categoria] || 0) + +c.valor;
    return acc;
  }, {});

  return (
    <div>
      {/* Presets */}
      <div className="rp-presets">
        {PRESETS.map((p, i) => (
          <button key={i} className={`btn btn-sm ${presetAtivo === i ? 'btn1' : 'btn3'}`}
            onClick={() => aplicarPreset(i)}>{p.label}</button>
        ))}
      </div>

      {/* Date range */}
      <form onSubmit={(e) => { e.preventDefault(); setPresetAtivo(-1); carregar(); }} className="mb2">
        <div className="rp-fl">
          <div className="inp-r" style={{ flex: 1, minWidth: 0 }}>
            <input className="inp" type="date" value={inicio} onChange={e => { setInicio(e.target.value); setPresetAtivo(-1); }} />
            <input className="inp" type="date" value={fim}    onChange={e => { setFim(e.target.value);   setPresetAtivo(-1); }} />
          </div>
          <button className="btn btn1 btn-sm" type="submit" disabled={loading}>
            {loading ? '⏳' : 'Filtrar'}
          </button>
        </div>
      </form>

      {erro && <div className="err">{erro}</div>}
      {loading && <div style={{ textAlign:'center', color:'var(--c4)', padding:'24px 0', fontSize:14 }}>Carregando...</div>}

      {dados && !loading && (
        <>
          {/* ── RESULTADO DESTAQUE ── */}
          <div className={`rp-resultado ${lucro >= 0 ? 'rp-resultado--ok' : 'rp-resultado--neg'}`}>
            <div className="rp-resultado-label">
              {lucro >= 0 ? '📈 Resultado do período' : '📉 Resultado do período'}
            </div>
            <div className="rp-resultado-valor">{fmt(lucro)}</div>
            <div className="rp-resultado-meta">
              {totalReceita > 0
                ? <>{margemPct >= 0 ? '✅' : '⚠️'} Margem de <strong>{margemPct.toFixed(1)}%</strong> sobre o faturamento</>
                : 'Nenhuma venda no período'
              }
            </div>
          </div>

          {/* ── KPI CARDS ── */}
          <div className="rp-kpi">
            <div className="rp-kpi-card rk-g">
              <span>💰 Receita</span>
              <b>{fmt(totalReceita)}</b>
              <small>{pedidos.length} pedido(s) · {totalUnids} un.</small>
            </div>
            <div className="rp-kpi-card rk-y">
              <span>📦 Custo insumos</span>
              <b>{fmt(totalInsumos)}</b>
              <small>{estoques.length} entrada(s) de estoque</small>
            </div>
            <div className="rp-kpi-card rk-r">
              <span>🧾 Despesas fixas</span>
              <b>{fmt(totalCustos)}</b>
              <small>{custos.length} lançamento(s)</small>
            </div>
            <div className="rp-kpi-card rk-b">
              <span>🎯 Ticket médio</span>
              <b>{fmt(ticketMedio)}</b>
              <small>por pedido no período</small>
            </div>
          </div>

          {/* ── BARRA PROPORCIONAL ── */}
          {barTotal > 0 && (
            <div className="rp-bar">
              <div className="rp-bar-label">
                <span><span className="rp-dot" style={{background:'var(--g)'}}/>Receita {pctReceita.toFixed(0)}%</span>
                <span><span className="rp-dot" style={{background:'var(--y)'}}/>Insumos {pctInsumos.toFixed(0)}%</span>
                <span><span className="rp-dot" style={{background:'var(--r)'}}/>Despesas {pctCustos.toFixed(0)}%</span>
              </div>
              <div className="rp-bar-track">
                <div className="rp-bar-seg" style={{ width:`${pctReceita}%`, background:'var(--g)' }} />
                <div className="rp-bar-seg" style={{ width:`${pctInsumos}%`, background:'var(--y)' }} />
                <div className="rp-bar-seg" style={{ width:`${pctCustos}%`, background:'var(--r)' }} />
              </div>
            </div>
          )}

          {/* ── SUB-TABS ── */}
          <div className="sub-tabs">
            {SUB.map(s => (
              <button key={s.key} className={subTab === s.key ? 'on' : ''} onClick={() => setSubTab(s.key)}>
                {s.label}
              </button>
            ))}
          </div>

          {/* ── PEDIDOS ── */}
          {subTab === 'pedidos' && (
            pedidos.length === 0
              ? <div className="mt"><span className="mt-ico">🛒</span>Nenhum pedido no período.</div>
              : <>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:'var(--c4)' }}>{pedidos.length} pedido(s)</span>
                    <span style={{ fontSize:13, fontWeight:800, color:'var(--g)' }}>{fmt(totalReceita)}</span>
                  </div>
                  <div className="lst">
                    {pedidos.slice((getPg('pedidos')-1)*PAGE_SIZE, getPg('pedidos')*PAGE_SIZE).map(p => (
                      <div key={`p-${p.id}`} className="it" style={{ cursor:'pointer' }}
                        onClick={async () => { try { const d=await getPedido(p.id); setPedidoModal(d); } catch {} }}>
                        <div className="it-main">
                          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                            <span className="bd bd-g">#{p.id}</span>
                            <span style={{ fontSize:12, color:'var(--c4)' }}>{new Date(p.data).toLocaleDateString('pt-BR')}</span>
                            {p.cliente_nome && <span style={{ fontSize:12, color:'var(--c4)' }}>· 👤 {p.cliente_nome}</span>}
                          </div>
                          <div style={{ fontSize:12, color:'var(--c4)', marginTop:2 }}>{p.quantidade_itens} un. · {p.qtd_produtos} produto(s)</div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontWeight:800, fontSize:15, color:'var(--g)' }}>{fmt(p.total)}</div>
                          <div style={{ fontSize:11, color:'var(--c4)' }}>ver ›</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Paginacao total={pedidos.length} page={getPg('pedidos')} onPage={p=>setPg('pedidos',p)} />
                </>
          )}

          {/* ── POR PRODUTO ── */}
          {subTab === 'margem' && (
            margem.length === 0
              ? <div className="mt"><span className="mt-ico">📊</span>Nenhuma venda no período.</div>
              : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {margem.map((p, idx) => {
                    const mg    = p.receita - p.custo;
                    const mgPct = p.receita > 0 ? (mg / p.receita) * 100 : 0;
                    const pctBar= maxReceita > 0 ? (p.receita / maxReceita) * 100 : 0;
                    return (
                      <div key={p.nome} className="rp-rank-item">
                        <div className="rp-rank-top">
                          <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                            <span className="rp-rank-num">#{idx+1}</span>
                            <strong style={{ fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.nome}</strong>
                          </div>
                          <div style={{ display:'flex', gap:10, flexShrink:0, alignItems:'center' }}>
                            <span style={{ fontSize:12, color:'var(--c4)' }}>{p.vendas} un.</span>
                            <span className={`bd ${mgPct >= 0 ? 'bd-g' : 'bd-r'}`}>{mgPct.toFixed(0)}% mg</span>
                          </div>
                        </div>
                        {/* barra de receita relativa */}
                        <div style={{ height:6, background:'var(--c2)', borderRadius:6, overflow:'hidden', margin:'8px 0 6px' }}>
                          <div style={{ height:'100%', width:`${pctBar}%`, background: mgPct>=40?'var(--g)':mgPct>=20?'var(--y)':'var(--r)', transition:'width .5s' }} />
                        </div>
                        <div className="rp-rank-vals">
                          <div><span>Receita</span><strong style={{ color:'var(--g)' }}>{fmt(p.receita)}</strong></div>
                          <div><span>Custo</span><strong>{fmt(p.custo)}</strong></div>
                          <div><span>Margem</span><strong style={{ color: mg>=0?'var(--g)':'var(--r)' }}>{fmt(mg)}</strong></div>
                          <div><span>Por un.</span><strong>{fmt(p.vendas>0 ? mg/p.vendas : 0)}</strong></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
          )}

          {/* ── ENTRADAS ── */}
          {subTab === 'estoque' && (
            estoques.length === 0
              ? <div className="mt"><span className="mt-ico">📦</span>Nenhuma entrada no período.</div>
              : <>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:'var(--c4)' }}>{estoques.length} entrada(s)</span>
                    <span style={{ fontSize:13, fontWeight:800, color:'var(--y)' }}>{fmt(totalInsumos)}</span>
                  </div>
                  <div className="lst">
                    {estoques.slice((getPg('estoque')-1)*PAGE_SIZE, getPg('estoque')*PAGE_SIZE).map(m => (
                      <div key={`m-${m.id}`} className="it">
                        <div className="it-main">
                          <strong style={{ fontSize:13 }}>{m.produto_nome}</strong>
                          <div style={{ fontSize:11, color:'var(--c4)', marginTop:2 }}>{new Date(m.data).toLocaleDateString('pt-BR')}</div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontWeight:700, fontSize:14 }}>{m.quantidade}x</div>
                          <div style={{ fontSize:13, fontWeight:700, color:'var(--y)' }}>{fmt(m.valor_total)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Paginacao total={estoques.length} page={getPg('estoque')} onPage={p=>setPg('estoque',p)} />
                </>
          )}

          {/* ── DESPESAS ── */}
          {subTab === 'custos' && (
            custos.length === 0
              ? <div className="mt"><span className="mt-ico">🧾</span>Nenhuma despesa no período.</div>
              : <>
                  {/* Resumo por categoria */}
                  {Object.keys(catCustos).length > 0 && (
                    <div className="rp-cat-row">
                      {Object.entries(catCustos).sort((a,b)=>b[1]-a[1]).map(([cat, val]) => (
                        <div key={cat} className="rp-cat-item">
                          <span>{cat}</span>
                          <strong style={{ color:'var(--r)' }}>{fmt(val)}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="lst" style={{ marginTop: 10 }}>
                    {custos.slice((getPg('custos')-1)*PAGE_SIZE, getPg('custos')*PAGE_SIZE).map(c => (
                      <div key={`c-${c.id}`} className="it">
                        <div className="it-main">
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span className="bd bd-r">{c.categoria}</span>
                            <span style={{ fontSize:11, color:'var(--c4)' }}>{fmtData(c.data)}</span>
                          </div>
                          <strong style={{ fontSize:13, marginTop:2, display:'block' }}>{c.descricao}</strong>
                        </div>
                        <div style={{ fontSize:15, fontWeight:800, color:'var(--r)', flexShrink:0 }}>{fmt(c.valor)}</div>
                      </div>
                    ))}
                  </div>
                  <Paginacao total={custos.length} page={getPg('custos')} onPage={p=>setPg('custos',p)} />
                </>
          )}
        </>
      )}

      {/* Modal pedido */}
      {pedidoModal && (
        <div className="ov" onClick={() => setPedidoModal(null)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-handle" />
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <h3>Pedido #{pedidoModal.id}</h3>
              <button className="btn btn3 btn-sm" onClick={() => setPedidoModal(null)}>✕</button>
            </div>
            <div style={{ display:'flex', gap:12, marginBottom:16, fontSize:13, color:'var(--c4)', flexWrap:'wrap' }}>
              <span>📅 {new Date(pedidoModal.data).toLocaleDateString('pt-BR')}</span>
              {pedidoModal.cliente_nome && <span>👤 {pedidoModal.cliente_nome}</span>}
              <span>📦 {pedidoModal.quantidade_itens} un.</span>
            </div>
            <div className="lst">
              {(pedidoModal.itens || []).map((item, i) => (
                <div key={i} className="it">
                  <div className="it-main">
                    <strong style={{ fontSize:14 }}>{item.produto_nome}</strong>
                    <div style={{ fontSize:12, color:'var(--c4)' }}>{item.quantidade}x · R$ {Number(item.preco_unitario).toFixed(2)} un.</div>
                  </div>
                  <strong style={{ color:'var(--g)', fontSize:15 }}>{fmt(item.subtotal)}</strong>
                </div>
              ))}
            </div>
            <div style={{ marginTop:16, padding:'12px 0 0', borderTop:'2px solid var(--g3)', textAlign:'right' }}>
              <div style={{ fontSize:12, color:'var(--c4)', marginBottom:2 }}>Total do pedido</div>
              <div style={{ fontSize:22, fontWeight:900, color:'var(--g)' }}>{fmt(pedidoModal.total)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
